import ReactMarkdown, { Components } from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';

// Custom schema that allows headings and common markdown elements
const schema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  ],
};

// Custom components with explicit styling
const components: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold mt-3 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-bold mt-3 mb-1 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</h4>,
  h5: ({ children }) => <h5 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h5>,
  h6: ({ children }) => <h6 className="text-sm font-medium mt-2 mb-1 first:mt-0">{children}</h6>,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children, className }) => {
    // Check if it's inline code (no className) or code block
    const isInline = !className;
    return isInline ? (
      <code className="px-1 py-0.5 rounded bg-[hsl(var(--background))] text-sm font-mono">{children}</code>
    ) : (
      <code className={className}>{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="p-3 rounded bg-[hsl(var(--background))] overflow-x-auto text-sm my-2">{children}</pre>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-[hsl(var(--primary))] underline hover:opacity-80" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="[&>p]:inline [&>p]:mb-0">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[hsl(var(--border))] pl-3 italic my-2">{children}</blockquote>
  ),
};

interface MarkdownPreviewProps {
  content: string;
  className?: string;
}

export function MarkdownPreview({ content, className }: MarkdownPreviewProps) {
  if (!content) return null;

  return (
    <div className={className}>
      <ReactMarkdown
        rehypePlugins={[[rehypeSanitize, schema], rehypeHighlight]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
